// src/pages/finance-portal/treasury-sweeps/TreasurySweepRules.js
//
// Stripe Treasury Sweep Automation (SDK-driven, no webhooks)
//
// Each rule references a Stripe Treasury FinancialAccount as the source and
// a Stripe PaymentMethod (target FA's PM or external bank PM) as the
// destination. The execute endpoint pulls the live balance via SDK, calls
// stripe.treasury.OutboundTransfer.create when the threshold is exceeded,
// and records an execution row.

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
    DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem,
    Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import SyncIcon from '@mui/icons-material/Sync';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    PAUSED: { bg: '#fef3c7', color: '#92400e' },
    DISABLED: { bg: '#f1f5f9', color: '#475569' },
    PENDING: { bg: '#dbeafe', color: '#1e40af' },
    SUCCESS: { bg: '#dcfce7', color: '#166534' },
    FAILED: { bg: '#fee2e2', color: '#991b1b' },
    SKIPPED: { bg: '#f1f5f9', color: '#475569' },
};

function formatCurrency(amount) {
    if (amount == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

const EMPTY_FORM = {
    name: '',
    description: '',
    source_financial_account_id: '',
    destination_payment_method: '',
    trigger_threshold: '',
    target_buffer: '',
    max_amount: '',
    min_transfer_amount: '',
    iso_currency_code: 'usd',
    frequency: 'DAILY',
    run_time: '06:00',
    run_day: 1,
};

export default function TreasurySweepRules() {
    const [rules, setRules] = useState([]);
    const [treasuryAccounts, setTreasuryAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dialog, setDialog] = useState({ open: false, form: { ...EMPTY_FORM }, editingId: null });
    const [executionsDialog, setExecutionsDialog] = useState({ open: false, ruleId: null, rows: [], loading: false });

    const fetchAll = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const [rulesRes, treasuryRes] = await Promise.all([
                axiosInstance.get('/api/finance/cash-position/sweep-rules'),
                axiosInstance.get('/api/finance/stripe/treasury/accounts').catch(() => ({ data: { data: [] } })),
            ]);
            if (rulesRes.data?.success) setRules(rulesRes.data.data || []);
            setTreasuryAccounts(treasuryRes.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load sweep rules.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const openCreate = () => setDialog({ open: true, form: { ...EMPTY_FORM }, editingId: null });

    const openEdit = (rule) => setDialog({
        open: true,
        editingId: rule.id,
        form: {
            name: rule.name,
            description: rule.description || '',
            source_financial_account_id: rule.source_financial_account_id || '',
            destination_payment_method: rule.destination_payment_method || '',
            trigger_threshold: rule.trigger_threshold,
            target_buffer: rule.target_buffer,
            max_amount: rule.max_amount || '',
            min_transfer_amount: rule.min_transfer_amount,
            iso_currency_code: rule.iso_currency_code || 'usd',
            frequency: rule.frequency,
            run_time: rule.run_time || '06:00',
            run_day: rule.run_day || 1,
        },
    });

    const handleSave = async () => {
        try {
            if (dialog.editingId) {
                await axiosInstance.put(`/api/finance/cash-position/sweep-rules/${dialog.editingId}`, dialog.form);
            } else {
                await axiosInstance.post('/api/finance/cash-position/sweep-rules', dialog.form);
            }
            setDialog({ open: false, form: { ...EMPTY_FORM }, editingId: null });
            setSuccess(dialog.editingId ? 'Sweep rule updated' : 'Sweep rule created');
            await fetchAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this sweep rule? Past executions are preserved.')) return;
        try {
            await axiosInstance.delete(`/api/finance/cash-position/sweep-rules/${id}`);
            await fetchAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Delete failed.');
        }
    };

    const handleRun = async (id) => {
        try {
            const res = await axiosInstance.post(`/api/finance/cash-position/sweep-rules/${id}/run`);
            await fetchAll();
            const exec = res.data?.data;
            if (exec) {
                setSuccess(`Sweep ${exec.status}: ${formatCurrency(exec.transfer_amount)}${exec.error_message ? ` — ${exec.error_message}` : ''}`);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Run failed.');
        }
    };

    const openExecutions = async (id) => {
        setExecutionsDialog({ open: true, ruleId: id, rows: [], loading: true });
        try {
            const res = await axiosInstance.get(`/api/finance/cash-position/sweep-rules/${id}/executions`);
            setExecutionsDialog({ open: true, ruleId: id, rows: res.data?.data || [], loading: false });
        } catch (err) {
            setExecutionsDialog({ open: true, ruleId: id, rows: [], loading: false });
            setError(err.response?.data?.message || 'Could not load executions.');
        }
    };

    const handleSyncExecutions = async (ruleId) => {
        try {
            const res = await axiosInstance.post(`/api/finance/cash-position/sweep-rules/${ruleId}/sync`);
            setExecutionsDialog(d => ({ ...d, rows: res.data?.data || d.rows }));
            setSuccess(`Refreshed ${res.data?.refreshed || 0} execution status(es) from Stripe`);
        } catch (err) {
            setError(err.response?.data?.message || 'Sync failed.');
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a' }}>
                        Treasury Sweep Rules
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                        Automated excess-cash transfers via Stripe Treasury OutboundTransfer.
                        Each rule moves funds from a Treasury financial account to a target
                        destination when the source balance crosses your threshold.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT }}
                    >
                        New Rule
                    </Button>
                    <IconButton onClick={fetchAll}><RefreshIcon /></IconButton>
                </Stack>
            </Box>

            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 0 }}>
                    {isLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={300} /></Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, color: '#475569', bgcolor: '#f8fafc' } }}>
                                    <TableCell>Rule</TableCell>
                                    <TableCell>Source FA → Destination PM</TableCell>
                                    <TableCell align="right">Trigger</TableCell>
                                    <TableCell align="right">Buffer</TableCell>
                                    <TableCell>Frequency</TableCell>
                                    <TableCell>Next Run</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rules.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5, fontFamily, color: '#94a3b8' }}>
                                            No sweep rules configured yet.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {rules.map((r) => (
                                    <TableRow key={r.id} hover>
                                        <TableCell sx={{ fontFamily }}>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                                                {r.name}
                                            </Typography>
                                            {r.description && (
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                    {r.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.78rem' }}>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <span style={{ fontFamily: 'monospace' }}>{r.source_financial_account_id}</span>
                                                <SwapHorizIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                                <span style={{ fontFamily: 'monospace' }}>{r.destination_payment_method}</span>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>
                                            {formatCurrency(r.trigger_threshold)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily }}>
                                            {formatCurrency(r.target_buffer)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily }}>
                                            <Chip label={r.frequency} size="small" sx={{ fontFamily, fontSize: '0.7rem', bgcolor: '#f1f5f9' }} />
                                            {r.run_time && (
                                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', mt: 0.5 }}>
                                                    {r.run_time} UTC
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#475569' }}>
                                            {r.next_run_at ? new Date(r.next_run_at).toLocaleString() : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={r.status}
                                                size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                                    bgcolor: STATUS_COLORS[r.status]?.bg, color: STATUS_COLORS[r.status]?.color,
                                                }}
                                            />
                                            {r.last_execution_status && (
                                                <Typography sx={{
                                                    fontFamily, fontSize: '0.7rem',
                                                    color: STATUS_COLORS[r.last_execution_status]?.color, mt: 0.5,
                                                }}>
                                                    Last: {r.last_execution_status}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Run now">
                                                <IconButton size="small" onClick={() => handleRun(r.id)}>
                                                    <PlayArrowIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Execution history">
                                                <IconButton size="small" onClick={() => openExecutions(r.id)}>
                                                    <HistoryIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton size="small" onClick={() => openEdit(r)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(r.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Form dialog */}
            <Dialog open={dialog.open} onClose={() => setDialog({ open: false, form: { ...EMPTY_FORM }, editingId: null })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {dialog.editingId ? 'Edit Sweep Rule' : 'New Sweep Rule'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField label="Name" value={dialog.form.name}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, name: e.target.value } })} fullWidth />
                        <TextField label="Description" value={dialog.form.description}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, description: e.target.value } })}
                            fullWidth multiline rows={2} />
                        <TextField
                            select={treasuryAccounts.length > 0}
                            label="Source Treasury FinancialAccount (fa_...)"
                            value={dialog.form.source_financial_account_id}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, source_financial_account_id: e.target.value } })}
                            fullWidth required
                            helperText="Stripe Treasury FA the funds come from"
                        >
                            {treasuryAccounts.map(a => (
                                <MenuItem key={a.id} value={a.id}>{a.id}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Destination Payment Method (pm_... or fa_...)"
                            value={dialog.form.destination_payment_method}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, destination_payment_method: e.target.value } })}
                            fullWidth required
                            helperText="Target FA's PaymentMethod or external bank PM" />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField label="Trigger Threshold" type="number" value={dialog.form.trigger_threshold}
                                    onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, trigger_threshold: e.target.value } })}
                                    fullWidth helperText="Sweep when source > this" />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Target Buffer" type="number" value={dialog.form.target_buffer}
                                    onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, target_buffer: e.target.value } })}
                                    fullWidth helperText="Leave this much in source" />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Max Amount" type="number" value={dialog.form.max_amount}
                                    onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, max_amount: e.target.value } })}
                                    fullWidth helperText="Cap per execution (optional)" />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Min Transfer" type="number" value={dialog.form.min_transfer_amount}
                                    onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, min_transfer_amount: e.target.value } })}
                                    fullWidth helperText="Skip if below this" />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField select label="Frequency" value={dialog.form.frequency}
                                    onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, frequency: e.target.value } })} fullWidth>
                                    <MenuItem value="DAILY">Daily</MenuItem>
                                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                                    <MenuItem value="ON_TRIGGER">On Trigger</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Run Time (HH:MM UTC)" value={dialog.form.run_time}
                                    onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, run_time: e.target.value } })} fullWidth />
                            </Grid>
                        </Grid>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog({ open: false, form: { ...EMPTY_FORM }, editingId: null })} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}
                        disabled={!dialog.form.name || !dialog.form.source_financial_account_id || !dialog.form.destination_payment_method}
                        sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT }}>
                        Save Rule
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Executions dialog */}
            <Dialog open={executionsDialog.open} onClose={() => setExecutionsDialog({ open: false, ruleId: null, rows: [], loading: false })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Sweep Execution History
                    {executionsDialog.ruleId && (
                        <Button startIcon={<SyncIcon />} size="small" onClick={() => handleSyncExecutions(executionsDialog.ruleId)}
                            sx={{ fontFamily, textTransform: 'none' }}>
                            Sync from Stripe
                        </Button>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {executionsDialog.loading ? (
                        <Skeleton variant="rounded" height={200} />
                    ) : executionsDialog.rows.length === 0 ? (
                        <Typography sx={{ fontFamily, color: '#94a3b8', textAlign: 'center', py: 3 }}>
                            No executions yet.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ '& th': { fontFamily, fontWeight: 700 } }}>
                                    <TableCell>Executed At</TableCell>
                                    <TableCell align="right">Source Before</TableCell>
                                    <TableCell align="right">Transfer</TableCell>
                                    <TableCell>Local Status</TableCell>
                                    <TableCell>Stripe Status</TableCell>
                                    <TableCell>OutboundTransfer ID</TableCell>
                                    <TableCell>Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {executionsDialog.rows.map(ex => (
                                    <TableRow key={ex.id}>
                                        <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{new Date(ex.executed_at).toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily }}>{formatCurrency(ex.source_balance_before)}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(ex.transfer_amount)}</TableCell>
                                        <TableCell><Chip label={ex.status} size="small" sx={{ fontFamily, fontSize: '0.7rem', bgcolor: STATUS_COLORS[ex.status]?.bg, color: STATUS_COLORS[ex.status]?.color }} /></TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.75rem', textTransform: 'capitalize' }}>{ex.stripe_status || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b' }}>{ex.stripe_outbound_transfer_id || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>{ex.error_message || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExecutionsDialog({ open: false, ruleId: null, rows: [], loading: false })} sx={{ fontFamily, textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
